/**
 * Financial statement sets (FSD 2.2.8 "Generate Statements" and "Publish";
 * FIN-F09-V07, AC03; FT12-B44).
 *
 * A set is one legal entity's profit and loss, balance sheet, cash flow and
 * equity movement for one fiscal period, as a numbered version:
 *
 *   generated -> published -> superseded
 *
 * Generation is allowed at any time and never replaces an earlier version.
 * Publication needs the period closed (V07), a second person (the publisher is
 * not the generator), and figures the ledger still yields; it makes the set
 * immutable, supersedes the period's earlier published version, attaches the
 * set to the period's evidence pack and distributes it to the audience.
 *
 * Calls (`@erpx/api`): `closeStatementSetsList`, `closeStatementSetsGenerate`,
 * `closeStatementSetsGet`, `closeStatementSetsPublish`. The PDF/XLSX download's
 * generated twin `closeStatementSetsDownload` returns `Promise<void>` and
 * discards the file, so the bytes come through the authenticated `rawRequest`
 * seam on the same path and query.
 */

import {
  closeStatementSetsGenerate,
  closeStatementSetsGet,
  closeStatementSetsList,
  closeStatementSetsPublish,
  type CloseStatementSetDTO,
  type CloseStatementSetsQuery,
} from '@erpx/api'

import { api, rawRequest } from '@/api/client'
import { MAX_LIMIT } from '@/composables/useTableUrlState'

export type { CloseStatementSetDTO }

export const STATEMENT_PERMISSIONS = {
  read: 'close.statements.read',
  generate: 'close.statements.generate',
  publish: 'close.statements.publish',
  evidenceRead: 'close.evidence.read',
  runRead: 'close.run.read',
} as const

export type StatementSetStatus = CloseStatementSetDTO['status']
export const STATEMENT_SET_STATUSES: readonly StatementSetStatus[] = ['generated', 'published', 'superseded']

export type StatementAudience = 'executive' | 'finance' | 'audit'
export const STATEMENT_AUDIENCES: readonly StatementAudience[] = ['executive', 'finance', 'audit']

export type StatementCompare = '' | 'prior_period' | 'prior_year'
export const STATEMENT_COMPARES: readonly StatementCompare[] = ['', 'prior_period', 'prior_year']

export type StatementFormat = 'pdf' | 'xlsx'

export const STATEMENT_KEYS = {
  all: ['close', 'statement-sets'] as const,
  page: (params: Record<string, unknown>) => ['close', 'statement-sets', 'list', params] as const,
  versions: (companyId: number, periodId: number) =>
    ['close', 'statement-sets', 'versions', companyId, periodId] as const,
  one: (id: number) => ['close', 'statement-sets', 'one', id] as const,
} as const

export const STATEMENT_ERROR_CODES = {
  periodNotClosed: 'ERR_PERIOD_NOT_CLOSED',
  stale: 'ERR_CLOSE_STATEMENTS_STALE',
  sod: 'ERR_SOD_VIOLATION',
  illegalTransition: 'ERR_ILLEGAL_TRANSITION',
} as const

export function statusTone(status: StatementSetStatus): 'success' | 'accent' | 'neutral' {
  if (status === 'published') return 'success'
  if (status === 'generated') return 'accent'
  return 'neutral'
}

/** V07: a set is published only once its period is closed (or locked). */
export function isPeriodClosed(status: string | undefined): boolean {
  return status === 'closed' || status === 'permanently_closed'
}

/* ------------------------------------------------------------------- reads */

export interface StatementSetFilters {
  companyId: number | null
  periodId: number | null
  status: string
  cursor: string
}

export interface StatementSetPage {
  items: CloseStatementSetDTO[]
  nextCursor: string | null
  hasMore: boolean
}

export function listStatementSets(filters: StatementSetFilters, limit = 50): Promise<StatementSetPage> {
  const query: CloseStatementSetsQuery = {
    limit,
    ...(filters.cursor === '' ? {} : { cursor: filters.cursor }),
    ...(filters.companyId === null ? {} : { companyId: filters.companyId }),
    ...(filters.periodId === null ? {} : { periodId: filters.periodId }),
    ...(filters.status === '' ? {} : { status: filters.status }),
  }
  return closeStatementSetsList(api(), query)
}

/** Every version of one entity's period, newest first by version: the history panel. */
export async function listVersions(companyId: number, periodId: number): Promise<CloseStatementSetDTO[]> {
  const out: CloseStatementSetDTO[] = []
  let cursor = ''
  for (let pageNo = 0; pageNo < 20; pageNo += 1) {
    const page = await closeStatementSetsList(api(), {
      companyId,
      periodId,
      limit: MAX_LIMIT,
      ...(cursor === '' ? {} : { cursor }),
    })
    out.push(...page.items)
    if (!page.hasMore || page.nextCursor === null || page.nextCursor === '') break
    cursor = page.nextCursor
  }
  return out.sort((a, b) => b.version - a.version)
}

export function getStatementSet(id: number): Promise<CloseStatementSetDTO> {
  return closeStatementSetsGet(api(), { id: String(id) })
}

/**
 * The highest published (or since superseded) version of a period. The server
 * publishes versions in order: an older version never replaces a newer one.
 */
export function highestPublishedVersion(versions: readonly CloseStatementSetDTO[]): number {
  return versions
    .filter((set) => set.status === 'published' || set.status === 'superseded')
    .reduce((highest, set) => Math.max(highest, set.version), 0)
}

/* ------------------------------------------------------------------ writes */

export function generateStatementSet(
  body: { companyId: number; periodId: number; compare: StatementCompare },
  idempotencyKey: string,
): Promise<CloseStatementSetDTO> {
  return closeStatementSetsGenerate(
    api(),
    {
      companyId: body.companyId,
      periodId: body.periodId,
      ...(body.compare === '' ? {} : { compare: body.compare }),
    },
    idempotencyKey,
  )
}

export function publishStatementSet(
  id: number,
  audience: StatementAudience,
  idempotencyKey: string,
): Promise<CloseStatementSetDTO> {
  return closeStatementSetsPublish(api(), { id: String(id) }, { audience }, idempotencyKey)
}

/* ---------------------------------------------------------------- download */

function filenameFrom(disposition: string | null, fallback: string): string {
  if (disposition === null) return fallback
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  const name = match?.[1]?.trim()
  if (name === undefined || name === '') return fallback
  try {
    return decodeURIComponent(name)
  } catch {
    return name
  }
}

/**
 * Download one rendering. A bearer-token app cannot use a bare link (it would
 * carry no credential), so the bytes are fetched through the authenticated
 * client and handed to the browser as a blob. The server audits the export
 * before the first byte.
 */
export async function downloadStatementSet(set: Pick<CloseStatementSetDTO, 'id' | 'setNo'>, format: StatementFormat): Promise<void> {
  const response = await rawRequest('GET', `/api/v1/close-statement-sets/${set.id}/download`, { format })
  const blob = await response.blob()
  const filename = filenameFrom(
    response.headers.get('Content-Disposition'),
    `${set.setNo === '' ? `statement-set-${set.id}` : set.setNo}.${format}`,
  )
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}
